/**
 * Landing Page Pricing Section
 * ZO Bull Algo Trading Platform
 * 
 * Pricing and FAQs loaded from CMS API
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface PricingPlan {
  name: string;
  nameKey: string;
  price: string;
  period: string;
  periodKey: string;
  description: string;
  descriptionKey: string;
  features: { text: string; textKey: string }[];
  cta: string;
  ctaKey: string;
  popular: boolean;
  is_trial?: boolean;
}

interface FAQ {
  question: string;
  questionKey: string;
  answer: string;
  answerKey: string;
}

export default function LandingPricing() {
  const { t } = useTranslation();
  const [plans, setPlans] = useState<PricingPlan[]>([
    {
      name: 'Free Trial', nameKey: 'freeTrial',
      price: '₹0', period: '1 Month', periodKey: 'oneMonth',
      description: 'Perfect for getting started.', descriptionKey: 'perfectForStarted',
      features: [
        { text: 'Full platform access', textKey: 'fullPlatformAccess' },
        { text: '3 Broker connections', textKey: 'brokerConnections3' },
        { text: 'Paper trading', textKey: 'paperTrading' },
        { text: 'Backtesting', textKey: 'backtesting' }
      ],
      cta: 'Start Free Trial', ctaKey: 'startFreeTrial',
      popular: false, is_trial: true
    },
    {
      name: 'Standard', nameKey: 'standard',
      price: '₹200', period: '/month', periodKey: 'perMonth',
      description: 'For active traders.', descriptionKey: 'forActiveTraders',
      features: [
        { text: 'Unlimited brokers', textKey: 'unlimitedBrokers' },
        { text: 'AI Copilot', textKey: 'aiCopilot' },
        { text: 'Real trading', textKey: 'realTrading' },
        { text: 'Priority support', textKey: 'prioritySupport' }
      ],
      cta: 'Get Started', ctaKey: 'getStarted',
      popular: true
    },
    {
      name: 'Premium', nameKey: 'premium',
      price: 'Custom', period: 'Contact us', periodKey: 'contactUs',
      description: 'For professionals.', descriptionKey: 'forProfessionals',
      features: [
        { text: 'Everything in Standard', textKey: 'everythingInStandard' },
        { text: 'White-label', textKey: 'whiteLabel' },
        { text: 'API access', textKey: 'apiAccess' }
      ],
      cta: 'Contact Sales', ctaKey: 'contactSales',
      popular: false
    }
  ]);

  const [faqs, setFaqs] = useState<FAQ[]>([
    { question: 'Can I switch plans anytime?', questionKey: 'switchPlansAnytime', answer: 'Yes, upgrade or downgrade anytime.', answerKey: 'switchPlansAnswer' },
    { question: 'What payment methods?', questionKey: 'paymentMethods', answer: 'Cards, UPI, net banking.', answerKey: 'paymentMethodsAnswer' },
    { question: 'Refund policy?', questionKey: 'refundPolicy', answer: '7-day money-back guarantee.', answerKey: 'refundPolicyAnswer' },
  ]);

  return (
    <section className="landing-pricing">
      <div className="section-container">
        <div className="section-header">
          <span className="section-badge">💰 {t('landing.pricing')}</span>
          <h2 className="section-title">{t('landing.pricingTitle')}</h2>
          <p className="section-subtitle">
            {t('landing.pricingSubtitle')}
          </p>
        </div>

        <div className="pricing-grid">
          {plans.map((plan, index) => (
            <div
              key={index}
              className={`pricing-card ${plan.popular ? 'popular' : ''}`}
            >
              {plan.popular && (
                <div className="popular-badge">{t('landing.mostPopular')}</div>
              )}
              <div className="pricing-header">
                <h3 className="plan-name">{t(`landing.${plan.nameKey}`)}</h3>
                <div className="plan-price">
                  <span className="price-value">{plan.price}</span>
                  <span className="price-period">{t(`landing.${plan.periodKey}`)}</span>
                </div>
                <p className="plan-description">{t(`landing.${plan.descriptionKey}`)}</p>
              </div>
              <ul className="plan-features">
                {plan.features.map((feature, i) => (
                  <li key={i} className="feature-item">
                    <span className="feature-check">✓</span>
                    {t(`landing.${feature.textKey}`)}
                  </li>
                ))}
              </ul>
              <Link
                to={plan.name === 'Premium' ? '/contact' : (plan.is_trial ? '/register' : '/login')}
                className={`btn ${plan.popular ? 'btn-primary' : 'btn-secondary'} btn-lg btn-full`}
              >
                {t(`landing.${plan.ctaKey}`)}
              </Link>
            </div>
          ))}
        </div>

        <div className="pricing-faq">
          <h3 className="faq-title">{t('landing.faqTitle')}</h3>
          <div className="faq-grid">
            {faqs.map((item, index) => (
              <div key={index} className="faq-item">
                <h4 className="faq-question">{t(`landing.${item.questionKey}`)}</h4>
                <p className="faq-answer">{t(`landing.${item.answerKey}`)}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="pricing-guarantee">
          <span className="guarantee-icon">🛡️</span>
          <div className="guarantee-content">
            <h4>{t('landing.moneyBackGuarantee')}</h4>
            <p>{t('landing.moneyBackDesc')}</p>
          </div>
        </div>
      </div>
    </section>
  );
}
